import React, { Fragment, useEffect, useState } from 'react';
import './gitfolio.scss';
import { BrowserRouter as Router, Route, Routes, Navigate, Outlet, useSearchParams, useLocation } from 'react-router-dom';
import { ThemeProvider, useTheme, type Theme } from './contexts/themeContext';
import { LanguageProvider } from './contexts/languageContext';
import type { Language } from './contexts/translations';
import Header from './components/header/header';
import Gitbody from './components/gitbody/gitbody';
import NavbarComponent from './components/navbar/navbar';
import { WindowSizeProvider } from './contexts/windowSizeContext';
import Body from './components/body/body';
import QRCode from 'react-qr-code';
import themecolor from './components/theme';
import TalkToMePage from './components/cv-chat/TalkToMePage';
import { trackPageView, startTimeOnPage, stopAndSendTimeOnPage } from './lib/analytics';
import ComplianceBanner from './components/compliance/ComplianceBanner';

const getHomepageBasename = (): string => {
  const pub = process.env.PUBLIC_URL || '/';
  try {
    // If PUBLIC_URL is a full URL, use only its pathname
    const url = /^https?:\/\//i.test(pub) ? new URL(pub, window.location.origin) : null;
    const path = (url ? url.pathname : pub) || '/';
    return path.replace(/\/$/, '') || '/';
  } catch {
    return '/';
  }
};

const Gitfolio: React.FC = () => {
  const homepageBasename = getHomepageBasename();
 
  return (
    <Router basename={homepageBasename}>
      <Routes>
        {/* Layout condiviso: provider di tema/lingua, navbar, analytics.
            Le rotte figlie ereditano tutto tramite Outlet. */}
        <Route element={<AppLayout />}>
          {/* Gestione via query string: ?theme=business|casual&lang=en|it */}
          <Route path="/" element={<ThemeContent />} />
          {/* Chat conversazionale sul CV */}
          <Route path="/talktome" element={<TalkToMePage />} />
        </Route>
        {/* Fallback su root: deve restare l'ultima rotta. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

function AppLayout() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const themeParam = searchParams.get('theme') || '';
  const langParam = searchParams.get('lang') || '';
  // Normalize params and validate
  const initialTheme: Theme = themeParam === 'business' || themeParam === 'casual' ? themeParam : 'business';
  const initialLanguage: Language = langParam === 'en' || langParam === 'it' ? langParam : 'en';

  // Ensure URL always includes normalized params for shareability
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (!next.get('theme') || (next.get('theme') !== 'business' && next.get('theme') !== 'casual')) {
      next.set('theme', initialTheme);
      changed = true;
    }
    if (!next.get('lang') || (next.get('lang') !== 'en' && next.get('lang') !== 'it')) {
      next.set('lang', initialLanguage);
      changed = true;
    }
    if (changed) {
      setSearchParams(next, { replace: true });
    }
    // We only want this to run on mount/search changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track page views on route changes (including query changes)
  useEffect(() => {
    try {
      const pathWithQuery = location.pathname + location.search;
      trackPageView(pathWithQuery);
      stopAndSendTimeOnPage();
      startTimeOnPage(pathWithQuery);
    } catch {
      // ignore
    }
  }, [location.pathname, location.search]);

  // Send time on page when page is hidden or before unload
  useEffect(() => {
    const onHide = () => stopAndSendTimeOnPage();
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onHide);
    window.addEventListener('beforeunload', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('beforeunload', onHide);
    };
  }, []);

  return (
    // Includere LanguageProvider e ThemeProvider qui sopra tutto il resto
    <WindowSizeProvider>
      <ThemeProvider key={initialTheme} initialTheme={initialTheme}>
        <LanguageProvider key={initialLanguage} initialLanguage={initialLanguage}>
          <NavbarComponent />
          <Outlet />
          <ComplianceBanner />
        </LanguageProvider>
      </ThemeProvider>
    </WindowSizeProvider>
  );
}

// Funzione separata per la logica condizionale
const ThemeContent = () => {
   const publicUrl = process.env.PUBLIC_URL || '/';
  const homepageUrl = /^https?:\/\//i.test(publicUrl)
    ? publicUrl
    : new URL(publicUrl, window.location.origin).href;
  const { theme } = useTheme(); // Ora questa parte è all'interno di ThemeProvider
  const [isHeaderChanged, setHeaderChange] = useState(false);
    const secondarycolor =
    theme === 'casual' ? themecolor.casualsecondary : themecolor.businesssecondary;
  return (
    <Fragment>
      <Header onHeadheightChange={setHeaderChange} />
      {/* ${!isHeaderChanged ? "vh-margin-top" : "less-margin-top"} */}
      <main
        className={`main-content ${!isHeaderChanged ? 'vh-margin-top' : 'less-margin-top'} no-print`}
      >
        {theme !== 'business' ? <Gitbody /> : <Body />}
      </main>
      <main className={`main-content main-content-printable less-margin-top `}>
        <Body />
      </main>
      {/*  <div style={{ height: "2000px" }}></div>Per creare lo scroll */}
       <footer className="text-center ">
        <div className='print-only mt-3 mb-2'>
<div style={{ height: "auto", margin: "0 auto", maxWidth: 64, width: "100%" }}>
  <QRCode
    size={256}
    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
    value={homepageUrl} 
    color={secondarycolor}
    viewBox={`0 0 256 256`}
  />
</div>

        </div>
        <div className='m-2'>
          &copy; {new Date().getFullYear()} - Developed by Luca Astolfi -{' '}
          <a href={homepageUrl} target="_blank" rel="noopener noreferrer">
            {homepageUrl}
          </a>
        </div>
      </footer>
    </Fragment>
 
  );
};

export default Gitfolio;
